/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useSubscriptionStatus from '../../hooks/useSubscriptionStatus';
import { getVendorStatus, updateVendorSubscriptionAutoRenew } from '../../services/subscriptionService';
import { useVendorAnalytics } from '../../services/vendorAnalyticsService';
import { useQuery, useMutation } from '@tanstack/react-query';
import api from '../../services/authService';
import { useVendorVerification } from '../../hooks/useVendorVerification';
import RenewalModal from '../../components/shared/RenewalModal';
import SubscriptionDetailsModal from '../../components/vendor/SubscriptionDetailsModal';
import EventReminderModal from '../../components/vendor/EventReminderModal';
import ConsultationCountdownBanner from '../../components/vendor/ConsultationCountdownBanner';
import CustomSelect from '../../components/shared/CustomSelect';
import AnnouncementCard from '../../components/shared/AnnouncementCard';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import { 
  Calendar, 
  DollarSign,
  Star,
  Crown,
  Bell,
  ArrowRight,
  Clock,
  Eye,
  Video,
  Shield
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useToastContext } from '../../contexts/ToastContext';

const VendorDashboard = () => {
  const { user } = useAuth();
  const { success, error: toastError } = useToastContext();
  const { hasActiveSubscription, isChecking, checkSubscriptionStatus } = useSubscriptionStatus();
  const { isVendorVerified, isVerificationPending, isLoading: verificationLoading } =
    useVendorVerification();
  const navigate = useNavigate();
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showEventReminderModal, setShowEventReminderModal] = useState(false);
  const [period, setPeriod] = useState('month');

  // Fetch detailed subscription information
  const fetchSubscriptionDetails = async () => {
    if (!user || user.user_type !== 'vendor') return;
    
    try {
      setLoading(true);
      const response = await getVendorStatus();
      if (response.success && response.data.current_subscription) {
        setSubscriptionDetails(response.data.current_subscription);
      } else {
        setSubscriptionDetails(null);
      }
    } catch (error) {
      console.error('Error fetching subscription details:', error);
    } finally {
      setLoading(false);
    }
  };

  const autoRenewMutation = useMutation({
    mutationFn: (nextAutoRenew) => updateVendorSubscriptionAutoRenew(nextAutoRenew),
    onSuccess: async (res) => {
      success('Subscription', res?.message || 'Preference saved.');
      await fetchSubscriptionDetails();
      checkSubscriptionStatus();
    },
    onError: (err) => {
      toastError(err?.message || 'Could not update auto-renewal.');
    },
  });

  // Fetch analytics
  const { data: analyticsData } = useVendorAnalytics(period);
  const analytics = analyticsData?.data || {};

  // Fetch announcements
  const { data: announcementsData } = useAnnouncements();
  const announcements = announcementsData?.data || [];

  // Fetch recent bookings
  const { data: bookingsData, isLoading: bookingsLoading } = useQuery({
    queryKey: ['vendorRecentBookings'],
    queryFn: async () => {
      const response = await api.get('/vendor/bookings?per_page=5&sort_by=created_at&sort_order=desc');
      return response.data;
    }
  });

  const recentBookings = bookingsData?.data?.data || bookingsData?.data || [];

  // Fetch booking statistics
  const { data: statsData } = useQuery({
    queryKey: ['vendorBookingStats'],
    queryFn: async () => {
      const response = await api.get('/vendor/bookings/statistics');
      return response.data;
    }
  });

  const stats = statsData?.data || {};

  // Fetch pending consultations
  const { data: consultationsData } = useQuery({
    queryKey: ['vendorConsultations', 'pending'],
    queryFn: async () => {
      const response = await api.get('/vendor/consultations', { params: { status: 'pending' } });
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
  });
  const pendingConsultations = consultationsData?.data?.data || consultationsData?.data || [];
  const pendingConsultationCount = pendingConsultations.length;

  // Fetch accepted consultations for countdown
  const { data: acceptedConsultationsData } = useQuery({
    queryKey: ['vendorConsultations', 'accepted'],
    queryFn: async () => {
      const response = await api.get('/vendor/consultations', {
        params: { status: 'accepted', per_page: 10 }
      });
      return response.data;
    },
    staleTime: 60 * 1000,
  });

  const acceptedConsultationsRaw =
    acceptedConsultationsData?.data?.data || acceptedConsultationsData?.data || [];

  const upcomingConsultation = React.useMemo(() => {
    if (!acceptedConsultationsRaw || acceptedConsultationsRaw.length === 0) return null;
    const now = new Date();

    const withDates = acceptedConsultationsRaw
      .map((c) => {
        if (!c.scheduled_date) return null;
        // Treat scheduled_date as date-only in local time to avoid timezone shifting issues.
        const dateStr = typeof c.scheduled_date === 'string'
          ? c.scheduled_date.slice(0, 10)
          : String(c.scheduled_date).slice(0, 10);
        const base = new Date(`${dateStr}T00:00:00`);
        if (Number.isNaN(base.getTime())) return null;
        if (c.scheduled_time) {
          const [hRaw, mRaw] = String(c.scheduled_time).split(':');
          const h = Number(hRaw || 0);
          const m = Number(mRaw || 0);
          base.setHours(h, m, 0, 0);
        } else {
          base.setHours(0, 0, 0, 0);
        }
        if (base <= now) return null;
        return { ...c, _targetDateTime: base };
      })
      .filter(Boolean);

    if (withDates.length === 0) return null;

    withDates.sort((a, b) => a._targetDateTime - b._targetDateTime);
    return withDates[0];
  }, [acceptedConsultationsRaw]);

  // Event reminder: fetch bookings from today through 5 days ahead (5th, 4th, 3rd, 2nd, 1st/last day)
  const getDateRange = () => {
    const today = new Date();
    const start = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const end = new Date(today);
    end.setDate(end.getDate() + 5);
    const endStr = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
    return { start, end: endStr };
  };
  const { start: reminderStartDate, end: reminderEndDate } = getDateRange();
  const { data: eventReminderData } = useQuery({
    queryKey: ['vendorBookings', 'eventReminder', reminderStartDate, reminderEndDate],
    queryFn: async () => {
      const response = await api.get('/vendor/bookings', {
        params: { start_date: reminderStartDate, end_date: reminderEndDate, per_page: 50 }
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const eventReminderBookingsRaw = (eventReminderData?.data?.data || eventReminderData?.data || []).filter(
    (b) => b.status !== 'cancelled'
  );
  // Group by days remaining: 5, 4, 3, 2, 1, 0 (today)
  const getDaysRemaining = (eventDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const evt = new Date(eventDate);
    evt.setHours(0, 0, 0, 0);
    return Math.round((evt - today) / (24 * 60 * 60 * 1000));
  };
  const eventReminderByDays = eventReminderBookingsRaw.reduce((acc, b) => {
    const days = getDaysRemaining(b.event_date);
    if (days >= 0 && days <= 5) {
      if (!acc[days]) acc[days] = [];
      acc[days].push(b);
    }
    return acc;
  }, {});
  const fiveDayReminderBookings = eventReminderBookingsRaw; // flat list for modal

  // Fetch subscription details when component mounts or subscription status changes
  useEffect(() => {
    if (hasActiveSubscription === true) {
      fetchSubscriptionDetails();
    }
  }, [hasActiveSubscription, user?.id]);

  // Auto-open 5-day event reminder modal once per day
  useEffect(() => {
    if (fiveDayReminderBookings.length === 0) return;
    const today = new Date().toDateString();
    const storageKey = `fiveDayReminderModal_${today}`;
    const alreadyShown = localStorage.getItem(storageKey);
    if (!alreadyShown) {
      setShowEventReminderModal(true);
    }
  }, [fiveDayReminderBookings.length]);

  const handleCloseEventReminderModal = () => {
    const today = new Date().toDateString();
    localStorage.setItem(`fiveDayReminderModal_${today}`, '1');
    setShowEventReminderModal(false);
  };

  // Helper functions
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '$0.00';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
    if (isNaN(numAmount) || !isFinite(numAmount)) return '$0.00';
    return `$${numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-purple-100 text-purple-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Check if subscription is expiring soon
  const isExpiringSoon = () => {
    if (!subscriptionDetails?.days_remaining) return false;
    return subscriptionDetails.days_remaining <= 5 && subscriptionDetails.days_remaining > 0;
  };

  const getUrgencyLevel = () => {
    if (!subscriptionDetails?.days_remaining) return null;
    if (subscriptionDetails.days_remaining <= 1) return 'critical';
    if (subscriptionDetails.days_remaining <= 3) return 'high';
    if (subscriptionDetails.days_remaining <= 5) return 'medium';
    return null;
  };

  const handleRenewalSuccess = async () => {
    await fetchSubscriptionDetails();
    checkSubscriptionStatus();
  };

  if (isChecking || loading || verificationLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            {/* <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Vendor Dashboard</h1> */}
            <p className="text-sm text-gray-600">Welcome back, {user?.name || 'Vendor'}!</p>
          </div>
          {hasActiveSubscription && subscriptionDetails && (
            <button
              onClick={() => setShowSubscriptionModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium mt-4 sm:mt-0"
            >
              <Crown className="w-4 h-4 text-primary-600" />
              <span>View Subscription</span>
            </button>
          )}
        </div>

        {/* Verification onboarding */}
        {!isVendorVerified && !isVerificationPending && (
          <div className="mb-6 rounded-lg border-l-4 border-blue-500 bg-blue-50 p-4">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 shrink-0 text-blue-600" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-blue-900">
                  Complete your vendor verification
                </h3>
                <p className="mt-1 text-sm text-blue-800">
                  Submit your business details so you can unlock full vendor features.
                </p>
                <button
                  onClick={() => navigate('/vendor/verification')}
                  className="mt-3 inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  Start verification
                </button>
              </div>
            </div>
          </div>
        )}

        {!isVendorVerified && isVerificationPending && (
          <div className="mb-6 rounded-lg border-l-4 border-amber-500 bg-amber-50 p-4">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 shrink-0 text-amber-600" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-amber-900">
                  Verification under review
                </h3>
                <p className="mt-1 text-sm text-amber-800">
                  Your submission is pending admin approval. We will notify you once approved.
                </p>
              </div>
            </div>
          </div>
        )}

        {hasActiveSubscription && subscriptionDetails?.is_free_tier && (
          <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex items-start gap-3">
                <Crown className="h-6 w-6 shrink-0 text-amber-700" />
                <div>
                  <h3 className="text-sm font-semibold text-amber-950">You are on the Free plan</h3>
                  <p className="mt-1 text-sm text-amber-900 max-w-xl">
                    Premium areas such as advanced analytics, custom branding, priority support, advanced booking,
                    and multi-location stay locked until you upgrade. Paid plans also raise your service and portfolio limits.
                  </p>
                </div>
              </div>
              <Link
                to="/vendor/subscription"
                className="inline-flex shrink-0 items-center justify-center rounded-lg bg-amber-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-amber-950 transition-colors"
              >
                Compare plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        )}

        {/* Subscription Expiring Warning */}
        {hasActiveSubscription && subscriptionDetails && isExpiringSoon() && (
          <div className={`mb-6 p-4 rounded-lg border-l-4 ${
            getUrgencyLevel() === 'critical' 
              ? 'bg-red-50 border-red-500' 
              : getUrgencyLevel() === 'high'
              ? 'bg-orange-50 border-orange-500'
              : 'bg-yellow-50 border-yellow-500'
          }`}>
            <div className="flex items-start space-x-3">
              <Bell className={`h-5 w-5 flex-shrink-0 ${
                getUrgencyLevel() === 'critical' 
                  ? 'text-red-500' 
                  : getUrgencyLevel() === 'high'
                  ? 'text-orange-500'
                  : 'text-yellow-500'
              } animate-pulse`} />
              <div className="flex-1">
                <h3 className={`text-sm font-medium ${
                  getUrgencyLevel() === 'critical' 
                    ? 'text-red-800' 
                    : getUrgencyLevel() === 'high'
                    ? 'text-orange-800'
                    : 'text-yellow-800'
                }`}>
                  {getUrgencyLevel() === 'critical' 
                    ? '⚠️ Critical: Subscription Expires Tomorrow!' 
                    : getUrgencyLevel() === 'high'
                    ? '🚨 High Priority: Subscription Expiring Soon!'
                    : '⚠️ Subscription Expiring Soon!'
                  }
                </h3>
                <p className={`text-sm mt-1 ${
                  getUrgencyLevel() === 'critical' 
                    ? 'text-red-700' 
                    : getUrgencyLevel() === 'high'
                    ? 'text-orange-700'
                    : 'text-yellow-700'
                }`}>
                  Your subscription expires in{' '}
                  <span className="font-semibold">
                    {subscriptionDetails.days_remaining} day{subscriptionDetails.days_remaining !== 1 ? 's' : ''}
                  </span>
                  . Renew {subscriptionDetails?.is_free_tier ? 'by choosing a paid plan' : 'now'} to avoid service interruption.
                </p>
                <button
                  onClick={() => {
                    if (subscriptionDetails?.is_free_tier) {
                      navigate('/vendor/subscription');
                      return;
                    }
                    setShowRenewalModal(true);
                  }}
                  className={`mt-3 inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    getUrgencyLevel() === 'critical' 
                      ? 'bg-red-600 text-white hover:bg-red-700' 
                      : getUrgencyLevel() === 'high'
                      ? 'bg-orange-600 text-white hover:bg-orange-700'
                      : 'bg-yellow-600 text-white hover:bg-yellow-700'
                  }`}
                >
                  <Crown className="h-4 w-4 mr-2" />
                  {subscriptionDetails?.is_free_tier ? 'Choose a paid plan' : 'Renew Subscription'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Consultation Countdown (next accepted consultation) */}
        {upcomingConsultation && (
          <ConsultationCountdownBanner
            consultation={upcomingConsultation}
            onViewConsultations={() => navigate('/vendor/consultations')}
          />
        )}

        {/* Pending Consultations Alert */}
        {pendingConsultationCount > 0 && (
          <div className="mb-6 p-4 rounded-lg border-l-4 bg-amber-50 border-amber-500 shadow-sm animate-slide-up overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-100/30 via-transparent to-transparent pointer-events-none" />
            <div className="relative flex items-start space-x-3">
              <div className="flex-shrink-0 p-2 rounded-full bg-amber-100/80 animate-pulse-slow">
                <Video className="h-5 w-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-amber-800">
                  📹 Pending Consultations – {pendingConsultationCount} request{pendingConsultationCount !== 1 ? 's' : ''} awaiting your response
                </h3>
                <p className="text-sm mt-1 text-amber-700">
                  Clients have requested consultations. Accept or reject to respond.
                </p>
                <div className="mt-3">
                  <button
                    onClick={() => navigate('/vendor/consultations')}
                    className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 shadow-sm"
                  >
                    <Video className="h-4 w-4 mr-2" />
                    View Consultations
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Event Reminder (5th, 4th, 3rd, 2nd, 1st/last day) */}
        {fiveDayReminderBookings.length > 0 && (
          <div className="mb-6 p-4 rounded-lg border-l-4 bg-indigo-50 border-indigo-500 shadow-sm animate-slide-up overflow-hidden relative">
            {/* Subtle gradient shine */}
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-100/30 via-transparent to-transparent pointer-events-none" />
            <div className="relative flex items-start space-x-3">
              <div className="flex-shrink-0 p-2 rounded-full bg-indigo-100/80 animate-pulse-slow">
                <Calendar className="h-5 w-5 text-indigo-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-indigo-800">
                  📅 Upcoming Events – {fiveDayReminderBookings.length} in the next 5 days
                </h3>
                <div className="space-y-1.5 mt-2 text-sm text-indigo-700">
                  {[0, 1, 2, 3, 4, 5].map((days) => {
                    const list = eventReminderByDays[days] || [];
                    if (list.length === 0) return null;
                    const label = days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${days} days away`;
                    return (
                      <div key={days} className="flex items-center gap-2 transition-colors duration-200 hover:text-indigo-800">
                        <span className="font-medium text-indigo-800 min-w-[100px]">{label}:</span>
                        <span>{list.length} event{list.length !== 1 ? 's' : ''}</span>
                        {list.length <= 2 && (
                          <span className="text-indigo-600">
                            ({list.map((b) => b.booking_reference || `#${b.id?.substring(0, 8)}`).join(', ')})
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => setShowEventReminderModal(true)}
                    className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 shadow-sm"
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    View Reminder
                  </button>
                  <button
                    onClick={() => navigate('/vendor/bookings')}
                    className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium bg-white border border-indigo-600 text-indigo-600 hover:bg-indigo-50 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View Bookings
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Announcements */}
        {announcements.length > 0 && (
          <div className="mb-6">
            {announcements.map((announcement) => (
              <AnnouncementCard
                key={announcement.id}
                announcement={announcement}
              />
            ))}
          </div>
        )}

        {/* Key Statistics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Bookings</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.total_bookings || analytics.total_bookings || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.completed_bookings || 0} completed
                </p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(stats.total_revenue || analytics.total_revenue || 0)}
                </p>
                <p className="text-xs text-gray-500 mt-1">All time</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Average Rating</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analytics.average_rating ? Number(analytics.average_rating).toFixed(1) : 'N/A'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {analytics.total_reviews || stats.total_reviews || 0} reviews
                </p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg">
                <Star className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Pending Bookings</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.pending_bookings || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">Requires attention</p>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Recent Bookings */}
          <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Bookings</h2>
              <Link
                to="/vendor/bookings"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
              >
                View All
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            {bookingsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-gray-100 rounded animate-pulse"></div>
                ))}
              </div>
            ) : recentBookings.length > 0 ? (
              <div className="space-y-3">
                {recentBookings.map((booking) => (
                  <div
                    key={booking.id}
                    onClick={() => navigate(`/vendor/bookings/${booking.id}`)}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-gray-900">
                          {booking.booking_reference || `#${booking.id.substring(0, 8)}`}
                        </p>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(booking.status)}`}>
                          {booking.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        {booking.client && (
                          <span>{booking.client.name}</span>
                        )}
                        {booking.event_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(booking.event_date)}
                          </span>
                        )}
                        {booking.vendor_payout_amount && (
                          <span className="font-medium text-gray-900">
                            {formatCurrency(booking.vendor_payout_amount)}
                          </span>
                        )}
                      </div>
                    </div>
                    <Eye className="w-5 h-5 text-gray-400" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No recent bookings</p>
              </div>
            )}
          </div>

          {/* Quick Actions & Subscription Summary */}
          <div className="space-y-6">
            {/* Subscription Summary Card */}
            {hasActiveSubscription && subscriptionDetails && (
              <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Crown className="w-5 h-5 text-primary-600" />
                    <h3 className="font-semibold text-gray-900">Subscription</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to="/vendor/plan"
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Plan Features
                    </Link>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => setShowSubscriptionModal(true)}
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      View Details
                    </button>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Current Plan</p>
                    <p className="text-lg font-bold text-primary-600">{subscriptionDetails.plan_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      subscriptionDetails.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {subscriptionDetails.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Days Remaining</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {subscriptionDetails.days_remaining ?? 0} days
                    </p>
                  </div>
                  {!subscriptionDetails.is_free_tier && subscriptionDetails.status === 'active' && (
                    <div className="flex items-center justify-between gap-4 pt-3 border-t border-gray-100">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">Auto-renewal</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          When on, your plan renews at the end of each billing period.
                          {subscriptionDetails.stripe_managed
                            ? ' This setting is synced with Stripe billing.'
                            : ''}
                        </p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={!!subscriptionDetails.auto_renew}
                        disabled={autoRenewMutation.isPending || loading}
                        onClick={() =>
                          autoRenewMutation.mutate(!subscriptionDetails.auto_renew)
                        }
                        className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-black disabled:opacity-50 disabled:cursor-not-allowed ${
                          subscriptionDetails.auto_renew ? 'bg-primary-600' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block mt-0.5 ml-0.5 h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${
                            subscriptionDetails.auto_renew ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  )}
                  {subscriptionDetails.usage && subscriptionDetails.limits && (
                    <div className="pt-3 border-t border-gray-100">
                      <p className="text-sm text-gray-600 mb-2">Plan Usage</p>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-700">Bookings this month</span>
                          <span className={subscriptionDetails.at_booking_limit ? 'font-medium text-amber-600' : 'text-gray-900'}>
                            {subscriptionDetails.usage.bookings_this_month}
                            {subscriptionDetails.limits.max_bookings_per_month != null
                              ? ` / ${subscriptionDetails.limits.max_bookings_per_month}`
                              : ' (unlimited)'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Services</span>
                          <span className={subscriptionDetails.at_service_limit ? 'font-medium text-amber-600' : 'text-gray-900'}>
                            {subscriptionDetails.usage.services_count}
                            {subscriptionDetails.limits.max_services != null
                              ? ` / ${subscriptionDetails.limits.max_services}`
                              : ' (unlimited)'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Portfolio items</span>
                          <span className={subscriptionDetails.at_portfolio_limit ? 'font-medium text-amber-600' : 'text-gray-900'}>
                            {subscriptionDetails.usage.portfolio_count}
                            {subscriptionDetails.limits.max_portfolio_items != null
                              ? ` / ${subscriptionDetails.limits.max_portfolio_items}`
                              : ' (unlimited)'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <Link
                  to="/vendor/bookings"
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm font-medium text-gray-900">View All Bookings</span>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </Link>
                <Link
                  to="/vendor/services"
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm font-medium text-gray-900">Manage Services</span>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </Link>
                <Link
                  to="/vendor/analytics"
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm font-medium text-gray-900">View Analytics</span>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </Link>
                <Link
                  to="/vendor/payments"
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm font-medium text-gray-900">Payment History</span>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Analytics Overview */}
        {analytics && Object.keys(analytics).length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Analytics Overview</h2>
              <div className="w-48">
                <CustomSelect
                  value={period}
                  onChange={(value) => setPeriod(value)}
                  options={[
                    { value: 'week', label: 'This Week' },
                    { value: 'month', label: 'This Month' },
                    { value: 'quarter', label: 'This Quarter' },
                    { value: 'year', label: 'This Year' }
                  ]}
                  size="sm"
                  className="w-full"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Bookings ({period})</p>
                <p className="text-xl font-bold text-gray-900">{analytics.total_bookings || 0}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Revenue ({period})</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatCurrency(analytics.total_revenue || 0)}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Avg Booking Value</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatCurrency(analytics.average_booking_value || 0)}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Conversion Rate</p>
                <p className="text-xl font-bold text-gray-900">
                  {analytics.booking_conversion_rate 
                    ? `${Number(analytics.booking_conversion_rate).toFixed(1)}%` 
                    : '0%'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Booking Status Breakdown */}
        {stats && Object.keys(stats).length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Booking Status</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{stats.pending_bookings || 0}</p>
                <p className="text-xs text-gray-600 mt-1">Pending</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{stats.confirmed_bookings || 0}</p>
                <p className="text-xs text-gray-600 mt-1">Confirmed</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-600">{stats.in_progress_bookings || 0}</p>
                <p className="text-xs text-gray-600 mt-1">In Progress</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{stats.completed_bookings || 0}</p>
                <p className="text-xs text-gray-600 mt-1">Completed</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-red-600">{stats.cancelled_bookings || 0}</p>
                <p className="text-xs text-gray-600 mt-1">Cancelled</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{stats.total_bookings || 0}</p>
                <p className="text-xs text-gray-600 mt-1">Total</p>
              </div>
            </div>
          </div>
        )}

        {/* Renewal Modal */}
        <RenewalModal
          isOpen={showRenewalModal}
          onClose={() => setShowRenewalModal(false)}
          subscriptionDetails={subscriptionDetails}
          onSuccess={handleRenewalSuccess}
        />

        {/* Subscription Details Modal */}
        <SubscriptionDetailsModal
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
          subscriptionDetails={subscriptionDetails}
          onRefresh={fetchSubscriptionDetails}
        />

        {/* Event Reminder Modal */}
        <EventReminderModal
          isOpen={showEventReminderModal}
          onClose={handleCloseEventReminderModal}
          bookingsByDays={eventReminderByDays}
          formatDate={formatDate}
        />
      </div>
    </div>
  );
};

export default VendorDashboard;
